import React, {
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useImperativeHandle,
  useRef,
} from "react";
import {
  Formik,
  FormikErrors,
  FormikProps,
  FormikState,
  FormikTouched,
} from "formik";
import * as yup from "yup";
import classNames from "classnames";
import OnahInput from "components/OnahInput";
import OnahButton from "components/OnahButton";
import { MessageParams } from "yup/lib/types";
import OnahRadio from "components/OnahRadio";
import { RadioChangeEvent } from "antd";
import OnahPhoneInput from "components/OnahPhoneInput";
import { isValidPhoneNumber } from "libphonenumber-js";
import OnahSelect, { OnahBankSelect, OnahCurrency, OnahSelectCountries } from "components/OnahSelect";
import OnahCheckBox from "components/OnahCheckBox";
import OnahFormObserver from "./OnahFormObserver";
import OnahDropdown from "components/OnahDropdown";
import { formateAmount, getFormattedAmountValue } from "helper";

yup.addMethod(yup.string, "isPhone", function (errorMessage) {
  return this.test(`test-phone`, errorMessage, function (value) {
    const { path, createError } = this;

    return !value
      ? true
      : isValidPhoneNumber(value ?? "") ||
          createError({ path, message: errorMessage });
  });
});

yup.addMethod(yup.string, "amount", function (errorMessage) {
  return this.test(`test-amount`, errorMessage, function (value) {
    const { path, createError } = this;

    return !value
      ? true
      : !isNaN(+getFormattedAmountValue(value)) ||
          createError({ path, message: errorMessage });
  });
});

yup.addMethod(yup.string, "minAmount", function (minAmount = 0, errorMessage) {
  return this.test(`test-minAmount`, errorMessage, function (value) {
    const { path, createError,  } = this;
    const num = +getFormattedAmountValue(value ?? '');
    return !value
      ? true
      : num >= Number(minAmount) ||
          createError({ path, message: errorMessage });
  });
});

yup.addMethod(yup.string, "maxAmount", function (maxAmount = 0, errorMessage) {
  return this.test(`test-maxAmount`, errorMessage, function (value) {
    const { path, createError} = this;
    const num = +getFormattedAmountValue(value ?? '');
    return !value
      ? true
      : num <= Number(maxAmount) ||
          createError({ path, message: errorMessage });
  });
});

type OnchangeHandler = (e: React.ChangeEvent<any> | RadioChangeEvent) => void;
type OnBlurHandler = (e: React.ChangeEvent<any>) => void;

/**
 * Initial value object
 */
type InitialValue = Record<string, string>;

type ValidationType =
  | "required"
  | "password"
  | "matches"
  | "min"
  | "max"
  | "compare"
  | "email"
  | "custom"
  | "url"
  | "workEmail"
  | "amount"
  | "minAmount"
  | "maxAmount"
  | "phone"
  | "date";

type InputType =
  | "input"
  | "amount"
  | "email"
  | "phone"
  | "password"
  | "dropdown"
  | "select"
  | "bankSelect"
  | "currencySelect"
  | "countrySelect"
  | "custom"
  | "radio"
  | "row"
  | "checkBox"
  | "date";

export type ValidationProps = {
  /**
   * Validation type
   */
  type: ValidationType;
  /**
   * if {@link ValidationProps.type type} requires a value, it's passed here
   */
  value?: string | RegExp | number | yup.StringSchema;

  message?: string;

  /**
   * the vale data type so that we can call yup.string() or yup.number() the first data type would be applied to all array
   */
  datatype?: 'string' | 'number' | 'boolean' 

  /**
   * disable a validation
   */
  disable?: boolean;
};

type InputProps = object & {
  /**
   * Formik error object
   */
  errors: FormikErrors<InitialValue>;
  /**
   * Formik input touched state
   */
  touched: FormikTouched<InitialValue>;

  label?: string;
  hideLabel?: boolean;
};

type Input<T = any> = {
  /**
   * Input type to render
   */
  input: InputType;
  /**
   * Input props need to be passed to the input
   */
  props?: Object;
  /**
   * form name which should be in {@link InitialValue InitialValue prop}
   */
  name: T;
  /**
   * Required if {@link Input.input input type} is ```custom```
   */
  content?: React.ReactNode;

  /**
   * Required if {@link Input.input input type} is ```row```
   */
  rowContent?: Array<Input<T>>;

  /**
   * Wrapper class name for this input.
   * To apply general style use {@link Props.inputWrapperClassName form inputWrapperClassName}
   */
  inputWrapperClassName?: string;

  label?: string;

  hideLabel?: boolean;

  /**
   * styling for input in a row content
   */
  rowItemClassName?: string;

  bottomContent?: ReactNode;
  topContent?: ReactNode;
};

export type OnahFormInput = Input;

type Props<T extends keyof InitialValue = any> = {
  /**
   * Form initial value
   */
  initialValue: InitialValue;
  /**
   * Validation to apply on each input key name should exist in {@link Props.initialValue InitialValue}
   */
  validations?: Record<T, Array<ValidationProps>>;
  /**
   * Submit handler
   */
  onSubmit: (data: Record<T, string>) => void;
  /**
   * The Form element
   */
  data: Array<Input<T>>;
  /**
   * Form class name
   */
  className?: string;
  /**
   * Global input wrapper className
   */
  inputWrapperClassName?: string;

  buttonClassName?: string;

  hideButton?: boolean;

  buttonText?: string;

  disableButton?: boolean;

  enableReinitialize?: boolean;

  onObserve?: (values: Record<string, any>) => void;

  spacing?: 'free' | 'tight';
};

export type IOnahForm = {
  reset: (nextState?: Partial<FormikState<InitialValue>> | undefined) => void;
  submit: () => void;
  validate: () => void;
  getValue: (key: string) => string;
  getValues: () => InitialValue;
  setValues: (values: Partial<InitialValue>) => void;
  getRef: () => FormikProps<InitialValue>;
};

const OnahForm: ForwardRefRenderFunction<IOnahForm, Props> = (
  {
    initialValue,
    validations = {},
    onSubmit,
    data,
    className,
    inputWrapperClassName,
    hideButton,
    buttonText = "Submit",
    disableButton,
    buttonClassName,
    enableReinitialize,
    onObserve,
    spacing = 'free',
  },
  ref
) => {
  const form = useRef<FormikProps<InitialValue>>(null);

  const constructErrorMessage = (msgParam: MessageParams, message?: string) => {
    if (!message) {
      return undefined;
    } else {
      return message.replace("%l%", msgParam.label);
    }
  };

  const getValidation = (
    validationType: ValidationProps,
    chain: yup.StringSchema
  ) => {
    if (validationType.disable) {
      return chain;
    }
    switch (validationType.type) {
      case "required":
        return chain.required(
          validationType.message
            ? (msgParam) =>
                constructErrorMessage(msgParam, validationType.message)
            : undefined
        );
      case "password":
        return chain
          .min(10)
          .matches(
            /^(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z])(?=.*[!,@,#,$,%,^,&,*,?,_,~,-,(,)]).{10,}$/,
            validationType.message
              ? (msgParam) =>
                  constructErrorMessage(msgParam, validationType.message)
              : "Password must contain at least on lowercase, one upper case and one of !,@,#,$,%,^,&,*,?,_,~,-,(,)"
          );
      case "min":
        return chain.min(
          Number(validationType.value),
          validationType.message
            ? (msgParam) =>
                constructErrorMessage(msgParam, validationType.message)
            : undefined
        );
      case "max":
        return chain.max(
          Number(validationType.value),
          validationType.message
            ? (msgParam) =>
                constructErrorMessage(msgParam, validationType.message)
            : undefined
        );
      case "matches":
        return chain.matches(
          validationType.value as RegExp,
          validationType.message
            ? (msgParam) =>
                constructErrorMessage(msgParam, validationType.message)
            : undefined
        );
      case "compare":
        return chain.oneOf(
          [yup.ref((validationType.value as string) ?? "")],
          validationType.message
            ? (msgParam) =>
                constructErrorMessage(msgParam, validationType.message)
            : undefined
        );
      case "email":
        return chain.email(
          validationType.message
            ? (msgParam) =>
                constructErrorMessage(msgParam, validationType.message)
            : undefined
        );
      case "custom":
        return chain.concat(validationType.value as yup.StringSchema);
      case "workEmail":
        return chain.matches(
          new RegExp(
            /^([\w.-]+)@(\[(\d{1,3}\.){3}|(?!hotmail|yopmail|gmail|googlemail|yahoo|gmx|ymail|outlook|bluewin|protonmail|t-online|web\.|online\.|aol\.|live\.)(([a-zA-Z\d-]+\.)+))([a-zA-Z]{2,63}|\d{1,3})(\]?)$/
          ),
          validationType.message
            ? (msgParam) =>
                constructErrorMessage(msgParam, validationType.message)
            : "Invalid Work Email"
        );
      case "url":
        return chain.matches(
          new RegExp(
            /^(https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/
          ),
          validationType.message
            ? (msgParam) =>
                constructErrorMessage(msgParam, validationType.message)
            : "Invalid URL"
        );
      case "date":
        return chain.matches(
          new RegExp(/^(0[1-9]|[12][0-9]|3[01])[/](0[1-9]|1[012])[/]\d{4}$/),
          validationType.message
            ? (msgParam) =>
                constructErrorMessage(msgParam, validationType.message)
            : "Invalid Date"
        );
      case "phone":
        // @ts-ignore
        return chain.isPhone(validationType.message ?? "Invalid phone number");
      case "amount":
        // @ts-ignore
        return chain.amount(validationType.message ?? "Invalid amount");
      case "minAmount":
        // @ts-ignore
        return chain.minAmount(
          validationType.value,
          validationType.message ?? `Minimum allowed amount is ${formateAmount(validationType.value as string ?? '')}`);
      case "maxAmount":
        // @ts-ignore
        return chain.maxAmount(
          validationType.value,
          validationType.message ?? `Maximum allowed amount is ${formateAmount(validationType.value as string ?? '')}`);
          
    }
  };

  const hasOverrideValidation = (key: string, type?: ValidationType) => {
    if (
      type &&
      validations[key]?.findIndex((validation) => validation.type === type) >= 0
    ) {
      return true;
    }
    return false;
  };

  const findInput = (
    inputData: Input<any>[],
    key: string
  ): Input<any> | undefined => {
    return inputData.find((input) => {
      if (input.input !== "row") {
        return input.name === key;
      } else if (input.input === "row") {
        return input.rowContent?.find((rowInput) => rowInput.name === key);
      }
      return false;
    });
  };

  const constructValidation = () => {
    const shape: Record<string, yup.AnySchema> = {};
    // used initialValue since it captures all input types because some inputs have predefined validations
    // which might not be captured if validations was used inside Object.keys
    Object.keys(initialValue).forEach((validationKey) => {
      const validation = validations[validationKey] ?? [];
      const dataType = validation.find(val => val.datatype)?.datatype;
      let chain = yup.string();
      if(dataType === 'number') {
        chain = yup.number() as any;
      } else if(dataType === 'boolean') {
        chain = yup.boolean() as any;
      }
      // let input = data.find(input => input.name === validationKey);
      let input = findInput(data, validationKey);
      if (input?.input === "row") {
        input = findInput(input.rowContent ?? [], validationKey);
      }
      let inputType = input?.input;
      if (input?.label && typeof input?.label === "string") {
        chain = chain.label(input.label);
      }
      // This comes before validation.forEach so that user can override this predefined check
      if (
        inputType === "email" &&
        !hasOverrideValidation(validationKey, "email")
      ) {
        chain = getValidation({ type: "email" }, chain);
      } else if (
        inputType === "password" &&
        !hasOverrideValidation(validationKey, "password")
      ) {
        chain = getValidation({ type: "password" }, chain);
      } else if (
        inputType === "phone" &&
        !hasOverrideValidation(validationKey, "phone")
      ) {
        chain = getValidation({ type: "phone" }, chain);
      } else if (
        inputType === "date" &&
        !hasOverrideValidation(validationKey, "date")
      ) {
        chain = getValidation({ type: "date" }, chain);
      } else if (
        inputType === "amount" &&
        !hasOverrideValidation(validationKey, "amount")
      ) {
        chain = getValidation({ type: "amount" }, chain);
      }
      validation.forEach((value) => {
        chain = getValidation(value, chain);
      });
      shape[validationKey] = chain;
    });
    let schema = yup.object().shape(shape);
    return schema;
  };

  useImperativeHandle(ref, () => ({
    reset(nextState) {
      form.current?.resetForm(nextState);
    },
    submit() {
      form.current?.submitForm();
    },
    validate() {
      form.current?.validateForm();
    },
    getValue(key) {
      return form.current?.values[key] || "";
    },
    getValues() {
      return form.current?.values || {};
    },
    setValues(newValues) {
      form.current?.setValues((oldValue) =>
        Object.create({ ...oldValue, ...newValues })
      );
    },
    getRef() {
      return form.current!;
    },
  }));

  const renderInput = (
    name: string,
    value: string,
    handleChange: OnchangeHandler,
    handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    const inputProps = p as any;
    return (
      <OnahInput
        {...p}
        label={hideLabel ? "" : props.label}
        name={name}
        onChange={(e) => {
          handleChange(e)
          if(inputProps && inputProps.onChange) {
            inputProps.onChange(e)
          }
        }}
        onBlur={(e) => {
          handleBlur(e);
          if(inputProps && inputProps.onBlur) {
            inputProps.onBlur(e)
          }
        }}
        value={value}
        key={name}
        error={errors[name] && touched[name] ? errors[name] : ""}
      />
    );
  };

  const renderPhone = (
    name: string,
    value: string,
    handleChange: OnchangeHandler,
    handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    const inputProps = p as any;
    return (
      <OnahPhoneInput
        {...p}
        label={hideLabel ? "" : props.label}
        name={name}
        onChange={(e) => {
          handleChange(e)
          if(inputProps && inputProps.onChange) {
            inputProps.onChange(e)
          }
        }}
        onBlur={(e) => {
          handleBlur(e);
          if(inputProps && inputProps.onBlur) {
            inputProps.onBlur(e)
          }
        }}
        value={value}
        key={name}
        error={errors[name] && touched[name] ? errors[name] : ""}
      />
    );
  };

  const renderEmail = (
    name: string,
    value: string,
    handleChange: OnchangeHandler,
    handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    const inputProps = p as any;
    return (
      <OnahInput
        {...p}
        label={hideLabel ? "" : props.label}
        name={name}
        type="email"
        onChange={(e) => {
          handleChange(e)
          if(inputProps && inputProps.onChange) {
            inputProps.onChange(e)
          }
        }}
        onBlur={(e) => {
          handleBlur(e);
          if(inputProps && inputProps.onBlur) {
            inputProps.onBlur(e)
          }
        }}
        value={value}
        key={name}
        error={errors[name] && touched[name] ? errors[name] : ""}
      />
    );
  };

  const renderDate = (
    name: string,
    value: string,
    handleChange: OnchangeHandler,
    handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    const inputProps = p as any;
    return (
      <OnahInput
        {...p}
        label={hideLabel ? "" : props.label}
        name={name}
        type="date"
        onChange={(e) => {
          handleChange(e)
          if(inputProps && inputProps.onChange) {
            inputProps.onChange(e)
          }
        }}
        onBlur={(e) => {
          handleBlur(e);
          if(inputProps && inputProps.onBlur) {
            inputProps.onBlur(e)
          }
        }}
        value={value}
        key={name}
        error={errors[name] && touched[name] ? errors[name] : ""}
      />
    );
  };

  const renderPassword = (
    name: string,
    value: string,
    handleChange: OnchangeHandler,
    handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    const inputProps = p as any;
    return (
      <OnahInput
        {...p}
        label={hideLabel ? "" : props.label}
        name={name}
        type="password"
        onChange={(e) => {
          handleChange(e)
          if(inputProps && inputProps.onChange) {
            inputProps.onChange(e)
          }
        }}
        onBlur={(e) => {
          handleBlur(e);
          if(inputProps && inputProps.onBlur) {
            inputProps.onBlur(e)
          }
        }}
        value={value}
        key={name}
        error={errors[name] && touched[name] ? errors[name] : ""}
      />
    );
  };

  const renderRadios = (
    name: string,
    value: string,
    handleChange: OnchangeHandler,
    handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    const inputProps = p as any;
    return (
      <OnahRadio
        {...p}
        label={hideLabel ? "" : props.label}
        name={name}
        onChange={(e) => {
          handleChange(e)
          if(inputProps && inputProps.onChange) {
            inputProps.onChange(e)
          }
        }}
        onBlur={(e) => {
          handleBlur(e);
          if(inputProps && inputProps.onBlur) {
            inputProps.onBlur(e)
          }
        }}
        value={value}
        key={name}
        error={errors[name] && touched[name] ? errors[name] : ""}
      />
    );
  };

  const renderContent = (content?: React.ReactNode) => {
    return content;
  };

  const renderRow = (
    rowContent: Array<Input>,
    values: InitialValue,
    handleChange: OnchangeHandler,
    handleBlur: OnBlurHandler,
    props: InputProps,
    input: Input,
  ) => {
    const { rowClassName = "", hideInnerTop = true } = props as any;
    return (
      <>
        {hideInnerTop ? null : input.topContent}
        <div
          className={classNames(
            "flex space-x-1 flex-wrap gap-y-1 items-start",
            rowClassName
          )}
        >
          {rowContent.map((content) => (
            <div
              className={classNames("flex-1", content.rowItemClassName)}
              key={content.name}
            >
              {renderFormItem(content, values, handleChange, handleBlur, {
                ...content.props,
                label: content.label,
                errors: props.errors,
                touched: props.touched,
                hideLabel: content.hideLabel,
              })}
            </div>
          ))}
        </div>
        {input.bottomContent}
      </>
    );
  };

  const renderSelect = (
    name: string,
    value: string,
    _handleChange: OnchangeHandler,
    _handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    const inputProps = p as any;
    return (
      <OnahSelect
        value={value}
        error={errors[name] && touched[name] ? errors[name] : ""}
        {...p}
        onBlur={(e) => {
          form.current?.setFieldTouched(name, true);
          if(inputProps && inputProps.onBlur) {
            inputProps.onBlur(e)
          }
        }}
        onChange={(value) => {
          form.current?.setFieldValue(name, value);
          if(inputProps && inputProps.onChange) {
            inputProps.onChange(value)
          }
        }}
        label={hideLabel ? "" : props.label}
      />
    );
  };

  const renderBankSelect = (
    name: string,
    value: string,
    _handleChange: OnchangeHandler,
    _handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    const inputProps = p as any;
    return (
      <OnahBankSelect
        value={value}
        error={errors[name] && touched[name] ? errors[name] : ""}
        {...p}
        onBlur={(e) => {
          form.current?.setFieldTouched(name, true);
          if(inputProps && inputProps.onBlur) {
            inputProps.onBlur(e)
          }
        }}
        onChange={(value) => {
          form.current?.setFieldValue(name, value);
          if(inputProps && inputProps.onChange) {
            inputProps.onChange(value)
          }
        }}
        label={hideLabel ? "" : props.label}
      />
    );
  };

  const renderCurrencySelect = (
    name: string,
    value: string,
    _handleChange: OnchangeHandler,
    _handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    const inputProps = p as any;
    return (
      <OnahCurrency
        value={value}
        error={errors[name] && touched[name] ? errors[name] : ""}
        {...p}
        onBlur={(e) => {
          form.current?.setFieldTouched(name, true);
          if(inputProps && inputProps.onBlur) {
            inputProps.onBlur(e)
          }
        }}
        onChange={(value) => {
          form.current?.setFieldValue(name, value);
          if(inputProps && inputProps.onChange) {
            inputProps.onChange(value)
          }
        }}
        label={hideLabel ? "" : props.label}
      />
    );
  };

  const renderCountrySelect = (
    name: string,
    value: string,
    _handleChange: OnchangeHandler,
    _handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    const inputProps = p as any;
    return (
      <OnahSelectCountries
        value={value}
        error={errors[name] && touched[name] ? errors[name] : ""}
        {...p}
        onBlur={(e) => {
          form.current?.setFieldTouched(name, true);
          if(inputProps && inputProps.onBlur) {
            inputProps.onBlur(e)
          }
        }}
        onChange={(value) => {
          form.current?.setFieldValue(name, value);
          if(inputProps && inputProps.onChange) {
            inputProps.onChange(value)
          }
        }}
        label={hideLabel ? "" : props.label}
      />
    );
  };

  const renderDropdown = (
    name: string,
    value: string,
    _handleChange: OnchangeHandler,
    _handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    const inputProps = p as any;
  
    return (
      <OnahDropdown
        value={value}
        wrapperClassName={classNames(
          className,
          "py-[14px] max-2xl:py-[12px] max-2xl!:h-[48px] !h-[50px]",
          "px-3 font-sans rounded-lg outline-none focus:!outline-none text-black border-0",
          "bg-gray-restingField [&>ant-dropdown-trigger]:bg-gray-restingField hover:!border-1 hover:!border-primary-ctaStroke",
          "focus:!border-primary-ctaStroke focus:!border items-start",
          errors ? "border-error-700 focus:border-error-700 border" : ""
        )}
        buttonClassName="w-full "
        textClassName="!font-semibold !font-sans text-blacktext-lg max-2xl:text-base"
        placeholderClassName="!text-gray-placeholder !font-medium !font-sans"
        error={errors[name] && touched[name] ? errors[name] : ""}
        {...inputProps}
        onOpenChange={() => form.current?.setFieldTouched(name, true)}
        onChange={(value) => {
          form.current?.setFieldValue(name, value);
            if(inputProps && inputProps.onChange) {
              inputProps.onChange(value);
            }
        }}
        label={hideLabel ? "" : props.label}
        options={inputProps.options ?? []}
      />
    );
  };

  const renderCheckBox = (
    name: string,
    value: string,
    _handleChange: OnchangeHandler,
    _handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    const { errors, touched, hideLabel, ...p } = props || {};
    return (
      <OnahCheckBox
        {...p}
        label={hideLabel ? "" : props.label}
        value={value}
        onToggle={(value) => {
          // @ts-ignore
          p.onToggle && p?.onToggle(value);
          form.current?.setFieldValue(name, value ? value : undefined);
        }}
        name={name}
        key={name}
        error={errors[name] && touched[name] ? errors[name] : ""}
      />
    );
  };

  const renderFormItem = (
    input: Input,
    values: InitialValue,
    handleChange: OnchangeHandler,
    handleBlur: OnBlurHandler,
    props: InputProps
  ) => {
    switch (input.input) {
      case "input":
      case "amount": {
        return renderInput(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          {
            ...props,
            // @ts-ignore
            format: input.input === 'input' ? '' : 'amount'}
        );
      }
      case "date":
        return renderDate(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          props
        );
      case "email":
        return renderEmail(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          props
        );
      case "password":
        return renderPassword(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          props
        );
      case "radio":
        return renderRadios(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          props
        );
      case "phone":
        return renderPhone(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          props
        );
      case "custom":
        return renderContent(input.content);
      case "row":
        return renderRow(
          input.rowContent ?? [],
          values,
          handleChange,
          handleBlur,
          props,
          input
        );
      case "select":
        return renderSelect(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          props
        );
      case "bankSelect":
        return renderBankSelect(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          props
        );
      case "currencySelect":
        return renderCurrencySelect(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          props
        );
      case "countrySelect":
        return renderCountrySelect(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          props
        );
      case "dropdown":
        return renderDropdown(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          props
        );
      case "checkBox":
        return renderCheckBox(
          input.name,
          values[input.name],
          handleChange,
          handleBlur,
          props
        );
      default:
        return null;
    }
  };

  return (
    <Formik
      initialValues={initialValue}
      enableReinitialize={enableReinitialize}
      validationSchema={constructValidation()}
      innerRef={form}
      onSubmit={async (values, { setSubmitting }) => {
        await onSubmit(values);
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValid,
      }) => {
        return (
          <form
            onSubmit={handleSubmit}
            className={classNames(className, "font-sans")}
          >
            {data.map((input) => (
              <div
                key={input.name}
                className={classNames(
                  inputWrapperClassName,
                  input.inputWrapperClassName,
                  spacing === 'tight' ? '2xl:mb-5 mb-3' : '2xl:mb-10 mb-8',
                  hideButton && "last:mb-0 last:max-2xl:mb-0"
                )}
              >
                {input.topContent}
                {renderFormItem(input, values, handleChange, handleBlur, {
                  ...input.props,
                  label: input.label,
                  hideLabel: input.hideLabel,
                  errors,
                  touched,
                })}
                {input.bottomContent}
              </div>
            ))}
            {!hideButton && (
              <OnahButton
                type="submit"
                disabled={disableButton}
                isLoading={isSubmitting}
                className={classNames("w-full", !isValid && 'opacity-40', buttonClassName)}
              >
                {buttonText}
              </OnahButton>
            )}
            <OnahFormObserver onObserve={onObserve} />
          </form>
        );
      }}
    </Formik>
  );
};

export default forwardRef(OnahForm);
